import { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faExclamationTriangle, faTags } from "@fortawesome/free-solid-svg-icons";
import moment from "moment";
import { useEventState } from "../functions/event-state";
import { useReservationState } from "../functions/reservation-state";
import { useEventHasPromotionCodesQuery, useGetPromotionCodeForReservationMutation } from "../../../api/promotions.api";
import { PromotionCodeDiscountType, PromotionCodeResult } from "../../../api/promotions.models";
import { currency } from "../../../common/helpers";
import { getInventoryType, getTotalNights } from "../functions/reservation-functions";
import { useEventInventoryQuery } from "../../../api/events.api";

const EventReservationPromotionCode = () => {
	const { event, selectedEventDate, selectedDateRange } = useEventState();
	const { reservation, updateReservation, useReservationValidationListener, validationErrors } = useReservationState();
	const { data: showPromotionCode } = useEventHasPromotionCodesQuery(event?.Id ?? 0);
	const { data: inventoryTypes } = useEventInventoryQuery(selectedEventDate, selectedDateRange);

	const { mutate: getPromotionCodeForReservation } = useGetPromotionCodeForReservationMutation();
	const [code, setCode] = useState("");
	const [result, setResult] = useState<PromotionCodeResult|undefined>();

	useEffect(() => {
		loadPromotionCode();
	}, []);

	useReservationValidationListener(() => {
		if (result != undefined && result.PromotionCode != undefined && result.PromotionCode.MinimumDays != undefined) {
			const nights = getTotalNights(reservation);
			if (nights < result.PromotionCode.MinimumDays) {
				setResult({ ...result, Success: false, Message: `This promotion code requires a minimum ${result.PromotionCode.MinimumDays} night booking.` });
				return;
			}
		}

		const promoCodeValidationErrors = validationErrors.filter(e => e.Code.startsWith("ERR_PROMO_CODE"));
		if (promoCodeValidationErrors.length > 0 && result) {
			setResult({ ...result, Success: false, Message: promoCodeValidationErrors[0].Message ?? "" });
		}
	});

	const loadPromotionCode = () => {
		if (reservation === undefined)
			return;

		if (reservation.PromotionCodeId !== undefined && reservation.PromotionCodeId !== 0) {
			setCode(reservation.PromotionCode?.Code ?? "");
			getPromotionCodeForReservation({ eventDateId: selectedEventDate?.Id ?? 0, code: reservation.PromotionCode?.Code ?? "", dateFrom: moment(selectedDateRange[0]).toDate(), dateTo: moment(selectedDateRange[1]).toDate() }, { 
				onSuccess: (promotionCodeResult) => {
					setResult(promotionCodeResult);
					setCode(reservation.PromotionCode?.Code ?? "");
					if (!promotionCodeResult.Success) {
						reservation.PromotionCodeId = 0;
						reservation.PromotionCode = undefined;
						updateReservation(reservation);
					}
				}
			});
		}
	}

	const handleApplyCode = () => {
		getPromotionCodeForReservation({ eventDateId: selectedEventDate?.Id ?? 0, code: code, dateFrom: moment(selectedDateRange[0]).toDate(), dateTo: moment(selectedDateRange[1]).toDate() }, { 
			onSuccess: (promotionCodeResult) => {

				setResult(promotionCodeResult);
				
				if (promotionCodeResult.Success && promotionCodeResult.PromotionCode != null) {
					if (promotionCodeResult.PromotionCode.MinimumDays != undefined) {
						const nights = getTotalNights(reservation);
						if (nights < promotionCodeResult.PromotionCode.MinimumDays) {
							setResult({ ...promotionCodeResult, Success: false, Message: `This promotion code requires a minimum ${promotionCodeResult.PromotionCode.MinimumDays} night booking.` });
							return;
						}
					}

					reservation.PromotionCodeId = promotionCodeResult.PromotionCode.Id;
					reservation.PromotionCode = promotionCodeResult.PromotionCode;
					updateReservation(reservation);
				}
			}
		})
	}

	const handleClearCode = () => {
		reservation.PromotionCodeId = 0;
		reservation.PromotionCode = undefined;
		updateReservation(reservation);
		setCode("");
		setResult(undefined);
	}

	if (showPromotionCode === false)
		return <></>

	return <>
		<div className="card shadow mb-3">
			<h5 className="card-header"><FontAwesomeIcon icon={faTags} className="text-muted mr-2" /> Discounts</h5>

			<div className="card-body">
				<p>If you have a promotional code, please enter it here:</p>
				<div className="row">
					<div className="col-12 col-sm-6">
						<div className="form-group mb-0">
							<input className="form-control" value={code} disabled={result?.Success === true} onChange={(e) => { setCode(e.target.value) }} name="promotionCode" maxLength={100} />
						</div>
					</div>
					<div className="col-6 col-sm-3">
						<button type="button" onClick={handleApplyCode} disabled={code.length === 0 || result?.Success === true} className="btn btn-primary font-weight-bold btn-block mt-2 mt-sm-0">Apply Code</button>
					</div>
					{(result !== undefined && result.Success === true || reservation.PromotionCode != undefined) &&
					<div className="col-6 col-sm-3">
						<button type="button" onClick={handleClearCode} className="btn btn-link mt-2 mt-sm-0">Remove</button>
					</div>
					}
				</div>

				{result && result.Success === false &&
				<div className="alert alert-warning pt-1 pb-1 pl-2 pr-2 mt-3 mb-0" role="alert">
					<FontAwesomeIcon icon={faExclamationTriangle} className="mr-2 text-warning" />
					{result.Message}
				</div>
				}
				{result && result.Success === true &&
					<div className="alert alert-success pt-1 pb-1 pl-2 pr-2 mt-3 mb-0" role="alert">
						<FontAwesomeIcon icon={faCheck} className="mr-2 text-success" />
						{result.PromotionCode?.DiscountType === PromotionCodeDiscountType.Amount &&
							<span>A discount of {currency.format(result.PromotionCode.DiscountAmount)} has been applied to your reservation!</span>
						}
						{result.PromotionCode?.DiscountType === PromotionCodeDiscountType.Percent &&
							<span>A discount of {result.PromotionCode.DiscountAmount * 1}% has been applied to your reservation!</span>
						}
						{result.PromotionCode?.DiscountType === PromotionCodeDiscountType.AccommodationPercent &&
							<span>A discount of {result.PromotionCode.DiscountAmount * 1}% off your {getInventoryType(reservation, inventoryTypes ?? []).toLowerCase().replaceAll(":","")} has been applied to your reservation!</span>
						}
					</div>
				}

			</div>
		</div>
	</>
}

export default EventReservationPromotionCode;